/* ==========================================================================
   Perfect Petals theme behaviour.

   Ported from the static build's pp.js. The interactions are the same; the
   difference is that the cart is Shopify's real cart (AJAX Cart API), not a
   localStorage prototype.

     - mobile nav and dropdowns
     - product gallery and variant (size) selector
     - VIP Flower Rush delivery selector with the daily cut-off window. Choosing
       VIP adds the VIP Flower Rush product to the cart; same-day removes it.
     - gift add-on buttons
     - cart page quantity, remove and card message
     - collection filter chips and scroll-loaded reveal (no Load More)
     - suburb checker, forms, carousels, read more, pop-up, honeypot, form tags

   Configuration is window.PP_CONFIG, written by snippets/pp-config.liquid from
   theme settings. No dependencies. Fails quietly if an element is not on the page.
   ========================================================================== */
(function () {
  'use strict';

  var CONFIG = window.PP_CONFIG || {};
  var DELIVERY = CONFIG.delivery || {};

  function $(selector, scope) { return (scope || document).querySelector(selector); }
  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  /* Shopify prices are integers in cents. */
  function money(cents) {
    var value = (Number(cents) || 0) / 100;
    return '$' + value.toLocaleString('en-AU', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  }

  function json(url, options) {
    options = options || {};
    options.headers = Object.assign({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }, options.headers || {});
    return fetch(url, options).then(function (response) {
      return response.json().then(function (body) {
        if (!response.ok) throw body;
        return body;
      });
    });
  }

  /* ---------------------------------------------------------------- toast */
  var toastEl = null;
  var toastTimer = null;

  function toast(message) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'pp-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-visible');
    }, 2600);
  }

  /* ----------------------------------------------------------------- cart */
  var Cart = {
    get: function () {
      return json((CONFIG.cartUrl || '/cart') + '.js');
    },

    /* items: [{ id: variantId, quantity: n, properties: {} }] */
    add: function (items) {
      return json(CONFIG.cartAddUrl + '.js', {
        method: 'POST',
        body: JSON.stringify({ items: items })
      }).then(function (result) {
        return Cart.refreshCount().then(function () { return result; });
      });
    },

    change: function (key, quantity) {
      return json(CONFIG.cartChangeUrl + '.js', {
        method: 'POST',
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (cart) {
        Cart.paintCount(cart.item_count);
        return cart;
      });
    },

    attributes: function (attributes) {
      return json(CONFIG.cartUpdateUrl + '.js', {
        method: 'POST',
        body: JSON.stringify({ attributes: attributes })
      });
    },

    hasVip: function (cart) {
      if (!DELIVERY.vipVariantId) return false;
      return cart.items.some(function (item) { return item.variant_id === DELIVERY.vipVariantId; });
    },

    refreshCount: function () {
      return Cart.get().then(function (cart) {
        Cart.paintCount(cart.item_count);
        return cart;
      });
    },

    paintCount: function (count) {
      $$('[data-pp-cart-count]').forEach(function (node) {
        node.textContent = count;
      });
    }
  };

  /* Apps add to the cart with their own requests (SellUp's "Add" does), so
     the header count would not move. Watch the page's network activity for
     any cart add or change, from anyone, and refresh the count after it. */
  (function watchAppCartChanges() {
    if (!('PerformanceObserver' in window)) return;
    var timer = null;
    try {
      new PerformanceObserver(function (list) {
        var hit = list.getEntries().some(function (entry) {
          return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(entry.name);
        });
        if (!hit) return;
        // On the cart page an app's add (SellUp's cart offers) must show up in
        // the line list, so reload. Updates are left alone: the card message
        // saves through /cart/update as it is typed.
        var added = list.getEntries().some(function (entry) {
          return /\/cart\/add(\.js)?(\?|$)/.test(entry.name);
        });
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (added && document.querySelector('[data-pp-cart]')) { window.location.reload(); return; }
          Cart.refreshCount().catch(function () {});
        }, 150);
      }).observe({ type: 'resource', buffered: false });
    } catch (err) { /* older browsers: the count updates on the next page */ }
  })();

  /* Keep the header count honest when a page comes back from the bfcache. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) Cart.refreshCount().catch(function () {});
  });

  /* --------------------------------------------------- express time window */
  /* The cut-off is the shop's, so read the clock in Brisbane time rather than
     the visitor's, whatever timezone their device is in. */
  function brisbaneNow() {
    try {
      var parts = new Intl.DateTimeFormat('en-AU', {
        timeZone: 'Australia/Brisbane', weekday: 'short',
        hour: 'numeric', minute: 'numeric', hour12: false
      }).formatToParts(new Date());
      var out = {};
      parts.forEach(function (p) { out[p.type] = p.value; });
      var days = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
      return { day: days[out.weekday], hour: parseInt(out.hour, 10) % 24, minute: parseInt(out.minute, 10) };
    } catch (err) {
      var now = new Date();
      return { day: now.getDay(), hour: now.getHours(), minute: now.getMinutes() };
    }
  }

  /* VIP window, in Brisbane time whatever the visitor's clock says: from the
     opening hour (8am) until the day's cut-off (Mon-Fri 1pm, Sat 11am, none on
     Sunday), all set in Theme settings > Perfect Petals - delivery. */
  function openHour() {
    var h = parseInt(DELIVERY.expressOpenHour, 10);
    return isNaN(h) ? 8 : h;
  }

  function cutoffFor(day) {
    var cutoff = (DELIVERY.expressCutoff || {})[day];
    return cutoff === undefined || cutoff === null ? null : cutoff;
  }

  function expressOpen() {
    if (!DELIVERY.vipVariantId || !DELIVERY.vipAvailable) return false;
    var now = brisbaneNow();
    var cutoff = cutoffFor(now.day);
    if (cutoff === null) return false;
    return now.hour >= openHour() && now.hour < cutoff;
  }

  function hourLabel(h) {
    var suffix = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + suffix;
  }

  /* The closed line names the next opening: "8am today" before opening, then
     "8am tomorrow", or the weekday name when tomorrow has no VIP (Sat -> Mon). */
  function expressClosedText() {
    var now = brisbaneNow();
    var time = hourLabel(openHour());
    var todayCutoff = cutoffFor(now.day);
    if (todayCutoff !== null && now.hour < openHour()) {
      return String(DELIVERY.earlyCopy || 'VIP Flower Rush opens at [time] today.').replace('[time]', time);
    }
    var names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var day = '';
    for (var i = 1; i <= 7; i++) {
      var d = (now.day + i) % 7;
      if (cutoffFor(d) !== null) { day = i === 1 ? 'tomorrow' : names[d]; break; }
    }
    var copy = todayCutoff === null
      ? (DELIVERY.offCopy || 'VIP Flower Rush is not available today. It is available again from [time] [day].')
      : (DELIVERY.closedCopy || 'VIP Flower Rush has closed for today. It is available again from [time] [day].');
    return String(copy).replace('[time]', time).replace('[day]', day).replace(/\s+\./, '.');
  }

  function expressEta() {
    var now = brisbaneNow();
    var total = now.hour * 60 + now.minute + (DELIVERY.expressBufferMinutes || 165);
    var h = Math.floor(total / 60) % 24;
    var m = total % 60;
    var suffix = h >= 12 ? 'pm' : 'am';
    var h12 = h % 12 === 0 ? 12 : h % 12;
    return h12 + ':' + (m < 10 ? '0' : '') + m + suffix;
  }

  function paintExpressWindow() {
    var rows = $$('[data-pp-express-row]');
    if (!rows.length) return;
    var open = expressOpen();

    $$('[data-pp-express-eta]').forEach(function (node) {
      node.textContent = open ? 'Order now, delivered by ' + expressEta() + ' today' : '';
    });

    $$('[data-pp-express-closed]').forEach(function (node) {
      node.hidden = open;
      if (!open) node.textContent = expressClosedText();
    });

    rows.forEach(function (row) {
      var input = $('input', row);
      row.classList.toggle('is-disabled', !open);
      if (!input) return;
      input.disabled = !open;
      if (!open && input.checked) {
        // Fall back to same-day so nobody can choose a service we cannot
        // deliver right now. On the cart this also removes the VIP line.
        var sameDay = $('input[value="same-day"]', row.closest('.pp-delivery'));
        if (sameDay) {
          sameDay.checked = true;
          sameDay.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    });
  }

  /* ------------------------------------------------------------------- nav */
  function initNav() {
    var toggle = $('[data-pp-navtoggle]');
    var nav = $('#pp-nav');
    if (toggle && nav) {
      toggle.addEventListener('click', function () {
        var open = nav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    // Dropdown submenus. CSS opens them on hover and focus-within; this adds
    // click for touch, and dismissal by Escape or clicking away.
    $$('[data-pp-submenu-toggle]').forEach(function (button) {
      var parent = button.closest('[data-pp-hasmenu]');
      var menu = $('[data-pp-subnav]', parent);
      if (!menu) return;
      button.addEventListener('click', function (event) {
        event.preventDefault();
        var open = !menu.classList.contains('is-open');
        closeMenus();
        menu.classList.toggle('is-open', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });

    function closeMenus() {
      $$('[data-pp-subnav]').forEach(function (m) { m.classList.remove('is-open'); });
      $$('[data-pp-submenu-toggle]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    }

    document.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('[data-pp-hasmenu]')) return;
      closeMenus();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeMenus();
    });
  }

  /* -------------------------------------------------- delivery radio rows */
  function paintRows(fieldset) {
    $$('.pp-delivery__row', fieldset).forEach(function (row) {
      var radio = $('input', row);
      row.classList.toggle('is-selected', !!radio && radio.checked);
    });
  }

  /* Store pickup (client 2026-10-14) is remembered for the visit, so a pickup
     chosen on a product page is still chosen on the cart. VIP stays in the
     cart itself, so only pickup needs remembering. */
  var METHOD_KEY = 'pp-delivery-method';
  function rememberMethod(value) {
    try { window.sessionStorage.setItem(METHOD_KEY, value); } catch (e) { /* storage blocked */ }
  }
  function rememberedMethod() {
    try { return window.sessionStorage.getItem(METHOD_KEY); } catch (e) { return null; }
  }

  function initDeliveryRows() {
    $$('.pp-delivery').forEach(function (fieldset) {
      var pickup = $('input[data-pp-delivery][value="pickup"]', fieldset);
      var express = $('input[data-pp-delivery][value="express"]', fieldset);
      if (pickup && rememberedMethod() === 'pickup' && !(express && express.checked)) pickup.checked = true;
      $$('input[data-pp-delivery]', fieldset).forEach(function (input) {
        input.addEventListener('change', function () {
          if (input.checked) rememberMethod(input.value);
          paintRows(fieldset);
        });
      });
      paintRows(fieldset);
    });
    paintExpressWindow();
  }

  /* Zapiet's cart picker has its own Local Delivery / Store Pickup buttons.
     The cart's delivery radios drive them, and a click on them updates the
     radios, so the two never disagree. */
  function zapietMethodButton(kind) {
    var icon = $('#storePickupApp .' + (kind === 'pickup' ? 'pickupIcon' : 'deliveryIcon'));
    return icon ? icon.closest('.checkoutMethod') : null;
  }

  // Zapiet renders after the page and can reset itself to its default method
  // while it starts up, so the wanted method is re-applied for ~10 seconds.
  // A customer's own click on Zapiet's buttons ends that straight away.
  var zapietWanted = null;
  var zapietTimer = null;
  function selectZapietMethod(kind) {
    zapietWanted = kind;
    var tries = 0;
    clearInterval(zapietTimer);
    zapietTimer = setInterval(function () {
      tries += 1;
      var button = zapietWanted && zapietMethodButton(zapietWanted);
      if (button && !button.classList.contains('active')) button.click();
      if (!zapietWanted || tries >= 40) clearInterval(zapietTimer);
    }, 250);
  }
  function releaseZapietMethod() {
    zapietWanted = null;
    clearInterval(zapietTimer);
  }

  /* ------------------------------------------------- SellUp VIP on products */
  /* SellUp owns VIP Flower Rush on the product page: its on-page offer (a
     toggle) is what adds VIP when SellUp handles Add to Cart. The design shows
     a delivery-speed picker instead, so SellUp's row is hidden and the picker
     drives its toggle. The picker also enforces the VIP cut-off, which SellUp's
     toggle does not. SellUp's Add to Cart pop-up lists VIP too; that item is
     hidden while the picker is on the page, so VIP is only chosen in one place. */
  function sellupVipBox() {
    if (!DELIVERY.vipHandle) return null;
    var item = document.getElementById('checkbox__' + DELIVERY.vipHandle);
    return item ? item.querySelector('input[type="checkbox"]') : null;
  }

  function initSellupVip() {
    var picker = $('.pp-product .pp-delivery');
    if (!picker || !DELIVERY.vipHandle) return;
    var express = $('input[value="express"]', picker);
    document.documentElement.classList.add('pp-vip-picker');

    function sync() {
      var box = sellupVipBox();
      if (!box) return;
      var row = box.closest('li');
      if (row) row.classList.add('pp-sellup-vip');
      // With VIP hidden, SellUp's on-page wrapper is often empty; drop it so it
      // does not add a gap under the buttons.
      var wrapper = box.closest('.luwc_class');
      if (wrapper) {
        var others = $$('li', wrapper).filter(function (li) { return !li.classList.contains('pp-sellup-vip'); });
        wrapper.classList.toggle('pp-sellup-empty', others.length === 0);
      }
      var want = !!(express && express.checked && !express.disabled);
      if (box.checked !== want) box.click();
    }

    picker.addEventListener('change', sync);

    // Re-check the window the moment Add to Cart or Buy It Now is pressed,
    // before SellUp's own click handler runs, so a page left open past the
    // cut-off cannot add VIP.
    document.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('[data-pp-product-form] [type="submit"]');
      if (!button) return;
      paintExpressWindow();
      sync();
    }, true);
    // SellUp renders its offers after the page loads.
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (sellupVipBox() || tries > 40) {
        clearInterval(timer);
        sync();
      }
    }, 500);
  }

  /* -------------------------------------------------------- product page */
  function initProduct() {
    var form = $('[data-pp-product-form]');

    // Gallery thumbnails
    var main = $('.pp-gallery__img');
    $$('[data-pp-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (!main) return;
        main.srcset = thumb.getAttribute('data-pp-thumb-srcset') || '';
        main.src = thumb.getAttribute('data-pp-thumb');
        $$('[data-pp-thumb]').forEach(function (other) {
          other.classList.toggle('is-selected', other === thumb);
        });
      });
    });

    if (!form) return;

    // Single-option products: one radio per variant, named "id", so the form
    // always posts the chosen variant, with or without JavaScript.
    // Multi-option products: one pill group per option; the variant is found
    // from the combination and written to the hidden id.
    var sizeInputs = $$('input[data-pp-size], input[data-pp-option]', form);
    var priceDisplay = $('[data-pp-price-display]');
    var pointsEl = $('[data-pp-points-earn]');
    var buttons = $$('[data-pp-primary], [data-pp-buynow]', form);
    var variantsEl = $('[data-pp-variants]', form);
    var variants = variantsEl ? JSON.parse(variantsEl.textContent) : null;
    var variantIdInput = $('[data-pp-variant-id]', form);

    function selectedId() {
      var checked = $('input[name="id"]:checked', form) || $('input[type="hidden"][name="id"]', form);
      return checked ? Number(checked.value) : null;
    }

    function chosenOptions() {
      return $$('[data-pp-option-group]', form).map(function (group) {
        var checked = $('input[data-pp-option]:checked', group);
        return checked ? checked.value : null;
      });
    }

    function findVariant(options) {
      return variants.filter(function (v) {
        return v.options.every(function (value, i) { return value === options[i]; });
      })[0] || null;
    }

    // Each option pill shows the price of the variant it would select with the
    // other options as they are, and reads as sold out if that one is.
    function paintOptionPills(options) {
      $$('input[data-pp-option]', form).forEach(function (input) {
        var index = Number(input.getAttribute('data-pp-option'));
        var combo = options.slice();
        combo[index] = input.value;
        var match = findVariant(combo);
        var label = input.closest('.pp-size');
        var priceEl = $('[data-pp-option-price]', label);
        if (priceEl) priceEl.textContent = match ? money(match.price) : '';
        label.classList.toggle('is-soldout', !match || !match.available);
      });
    }

    // The chosen variant as { id, price, available, image }.
    function current() {
      if (variants) {
        var options = chosenOptions();
        paintOptionPills(options);
        var v = findVariant(options);
        if (variantIdInput && v) variantIdInput.value = v.id;
        return v ? { id: v.id, price: v.price, available: v.available, image: v.image, missing: false } : { missing: true };
      }
      var input = $('input[data-pp-size]:checked', form);
      if (!input) return null;
      return {
        id: input.value,
        price: Number(input.getAttribute('data-pp-price')),
        available: input.getAttribute('data-pp-available') === 'true',
        image: input.getAttribute('data-pp-image')
      };
    }

    function update(fromUser) {
      $$('.pp-size', form).forEach(function (label) {
        var radio = $('input', label);
        label.classList.toggle('is-selected', !!radio && radio.checked);
      });
      var v = current();
      if (!v) return;
      if (v.missing) {
        // This combination doesn't exist as a variant.
        buttons.forEach(function (b) {
          b.disabled = true;
          if (b.hasAttribute('data-pp-primary')) b.textContent = 'Unavailable';
        });
        return;
      }
      // "From" stays until the customer picks a size themselves.
      if (priceDisplay) {
        if (fromUser) priceDisplay.removeAttribute('data-pp-from');
        priceDisplay.textContent = (priceDisplay.hasAttribute('data-pp-from') ? 'From ' : '') + money(v.price);
      }
      if (pointsEl) pointsEl.textContent = Math.round(v.price / 100 * (CONFIG.pointsPerDollar || 1));
      buttons.forEach(function (b) {
        b.disabled = !v.available;
        if (b.hasAttribute('data-pp-primary')) b.textContent = v.available ? b.getAttribute('data-pp-label') : 'Sold out';
      });
      if (fromUser && v.image && main) {
        main.srcset = '';
        main.src = v.image;
      }
      // Keep the URL on the chosen variant, as Dawn does, so it can be shared
      // and so apps reading ?variant= stay in step.
      if (fromUser && window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    sizeInputs.forEach(function (control) {
      control.addEventListener('change', function () { update(true); });
    });

    function wantsVip() {
      // When SellUp's VIP offer is on the page, SellUp adds VIP (the picker
      // ticks its toggle), so adding it here as well would double it.
      if (sellupVipBox()) return false;
      var express = $('input[data-pp-delivery][value="express"]', form.closest('.pp-product') || document);
      return !!(express && express.checked && !express.disabled && DELIVERY.vipVariantId);
    }

    function lineItems(quantity) {
      var item = { id: selectedId(), quantity: quantity || 1 };
      // Carry any line item properties an app or block has added to the form.
      var properties = {};
      $$('[name^="properties["]', form).forEach(function (field) {
        if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
        var key = field.name.slice(11, -1);
        if (key) properties[key] = field.value;
      });
      if (Object.keys(properties).length) item.properties = properties;
      return [item];
    }

    function addWithVip(items) {
      if (!wantsVip()) return Cart.add(items);
      return Cart.get().then(function (cart) {
        if (!Cart.hasVip(cart)) items.push({ id: DELIVERY.vipVariantId, quantity: 1 });
        return Cart.add(items);
      });
    }

    // Add to Cart stays on the page. Buy It Now adds then goes to the cart.
    // Apps that listen for the product form submit (SellUp's on-page offers)
    // still see a submit event, because the buttons are real submit buttons.
    form.addEventListener('submit', function (event) {
      var submitter = event.submitter || $('[data-pp-primary]', form);
      if (event.defaultPrevented) return;
      event.preventDefault();
      var goToCart = submitter && submitter.hasAttribute('data-pp-buynow');
      var qtyInput = $('input[name="quantity"]', form);
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      buttons.forEach(function (b) { b.setAttribute('aria-busy', 'true'); });

      addWithVip(lineItems(qty)).then(function () {
        if (goToCart) { window.location.href = CONFIG.cartUrl; return; }
        var title = form.getAttribute('data-pp-title') || 'Item';
        toast(title + ' added to your cart');
      }).catch(function (err) {
        toast((err && (err.description || err.message)) || 'Sorry, that could not be added');
      }).then(function () {
        buttons.forEach(function (b) { b.removeAttribute('aria-busy'); });
      });
    });

    update(false);
  }

  /* ----------------------------------------------------- gift add-on buttons */
  function initAddButtons() {
    $$('[data-pp-add]').forEach(function (button) {
      button.addEventListener('click', function () {
        var id = Number(button.getAttribute('data-pp-add'));
        if (!id) return;
        button.disabled = true;
        Cart.add([{ id: id, quantity: 1 }]).then(function () {
          toast(button.getAttribute('data-pp-title') + ' added to your cart');
          button.classList.add('is-added');
          button.textContent = 'Added';
          if (button.hasAttribute('data-pp-reload')) { window.location.reload(); return; }
          setTimeout(function () {
            button.classList.remove('is-added');
            button.textContent = '+ Add';
            button.disabled = false;
          }, 2000);
        }).catch(function (err) {
          button.disabled = false;
          toast((err && err.description) || 'Sorry, that could not be added');
        });
      });
    });
  }

  /* ------------------------------------------------------------ cart page */
  function initCartPage() {
    var page = $('[data-pp-cart]');
    if (!page) return;

    function busy(on) { page.classList.toggle('is-busy', on); }

    $$('[data-pp-qty]', page).forEach(function (button) {
      button.addEventListener('click', function () {
        var key = button.getAttribute('data-pp-qty');
        var next = parseInt(button.getAttribute('data-pp-next'), 10);
        busy(true);
        Cart.change(key, Math.max(0, next)).then(function () {
          window.location.reload();
        }).catch(function (err) {
          busy(false);
          toast((err && err.description) || 'Sorry, that could not be updated');
        });
      });
    });

    $$('[data-pp-remove]', page).forEach(function (button) {
      button.addEventListener('click', function () {
        busy(true);
        Cart.change(button.getAttribute('data-pp-remove'), 0).then(function () {
          window.location.reload();
        });
      });
    });

    // Pickup on the cart: open Zapiet on Store Pickup, and follow Zapiet's own
    // method buttons back into the radios.
    var pickupRadio = $('input[data-pp-delivery][value="pickup"]', page);
    if (pickupRadio) {
      // The totals row names the method: same-day (fee at checkout) or pickup (free).
      var methodLabel = $('[data-pp-method-label]', page);
      var methodFee = $('[data-pp-method-fee]', page);
      var labelText = methodLabel && [methodLabel.textContent, methodLabel.getAttribute('data-pp-pickup-label')];
      var feeText = methodFee && [methodFee.textContent, methodFee.getAttribute('data-pp-pickup-fee')];
      // With Zapiet's Delivery / Pickup buttons now at the top of the summary
      // (client 2026-10-20), the delivery speed choice only shows for delivery.
      var summary = pickupRadio.closest('.pp-summary');
      var paintMethod = function () {
        var i = pickupRadio.checked ? 1 : 0;
        if (methodLabel) methodLabel.textContent = labelText[i];
        if (methodFee) methodFee.textContent = feeText[i];
        if (summary) summary.classList.toggle('is-pickup', pickupRadio.checked);
      };
      pickupRadio.closest('.pp-delivery').addEventListener('change', paintMethod);
      paintMethod();
      selectZapietMethod(pickupRadio.checked ? 'pickup' : 'delivery');
      document.addEventListener('click', function (event) {
        var method = event.target.closest && event.target.closest('#storePickupApp .checkoutMethod');
        if (!method || !event.isTrusted) return;
        releaseZapietMethod();
        var isPickup = !!$('.pickupIcon', method);
        if (isPickup === pickupRadio.checked) return;
        var target = isPickup ? pickupRadio : $('input[data-pp-delivery][value="same-day"]', page);
        if (!target) return;
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // Delivery speed on the cart adds or removes the VIP Flower Rush line.
    $$('input[data-pp-delivery]', page).forEach(function (input) {
      input.addEventListener('change', function () {
        if (!input.checked) return;
        selectZapietMethod(input.value === 'pickup' ? 'pickup' : 'delivery');
        if (!DELIVERY.vipVariantId) return;
        var wantVip = input.value === 'express';
        Cart.get().then(function (cart) {
          var has = Cart.hasVip(cart);
          if (wantVip === has) return null;
          busy(true);
          if (wantVip) return Cart.add([{ id: DELIVERY.vipVariantId, quantity: 1 }]);
          var line = cart.items.filter(function (item) { return item.variant_id === DELIVERY.vipVariantId; })[0];
          return Cart.change(line.key, 0);
        }).then(function (result) {
          if (result) window.location.reload();
        }).catch(function () { busy(false); });
      });
    });

    // Card message saves as it is typed, so it survives a reload (quantity
    // changes reload the page) and reaches the order as a cart attribute.
    var message = $('[data-pp-card-message]', page);
    if (message) {
      var timer = null;
      message.addEventListener('input', function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          var attrs = {};
          attrs[message.getAttribute('data-pp-attribute')] = message.value;
          Cart.attributes(attrs).catch(function () {});
        }, 400);
      });
    }

    // Title, Occasion and Occasion Date save as soon as they change, like the
    // card message, so a reload or Zapiet's own checkout hand-off keeps them.
    var details = $('[data-pp-cart-details]', page);
    if (details) {
      var detailsError = $('[data-pp-cart-details-error]', details);
      var detailFields = $$('[data-pp-attribute]', details);
      // All three go up together, one request at a time: separate updates sent
      // close together race on Shopify's side and the last one wins.
      var saving = Promise.resolve();
      var saveDetails = function () {
        var attrs = {};
        detailFields.forEach(function (f) { attrs[f.getAttribute('data-pp-attribute')] = f.value; });
        saving = saving.then(function () { return Cart.attributes(attrs); }).catch(function () {});
        return saving;
      };
      detailFields.forEach(function (field) {
        field.addEventListener('change', function () {
          field.classList.remove('is-invalid');
          if (detailsError && !$('.is-invalid', details)) detailsError.hidden = true;
          saveDetails();
        });
      });

      // Title and Occasion are required before checkout. The check runs in the
      // capture phase so it beats Zapiet's checkout handler, which can submit the
      // form itself (a programmatic submit fires no submit event to catch).
      var missingDetails = function () {
        var missing = $$('[data-pp-cart-required]', details).filter(function (field) { return !field.value; });
        missing.forEach(function (field) { field.classList.add('is-invalid'); });
        return missing;
      };
      var blockCheckout = function (event) {
        var missing = missingDetails();
        if (!missing.length) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (detailsError) detailsError.hidden = false;
        details.scrollIntoView({ behavior: 'smooth', block: 'center' });
        missing[0].focus({ preventScroll: true });
      };
      document.addEventListener('click', function (event) {
        var button = event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"]');
        if (button && page.contains(button)) blockCheckout(event);
      }, true);
      page.addEventListener('submit', function (event) {
        if (event.submitter && event.submitter.name === 'checkout') blockCheckout(event);
      }, true);
    }
  }

  /* --------------------------------------------------- announcement bar */
  /* On phones the announcement messages show one at a time (QC #7). They
     rotate every few seconds, pause while hovered or focused, and stay on the
     first message for visitors who prefer reduced motion. */
  function initAnnouncement() {
    var bar = $('[data-pp-announce]');
    if (!bar || !window.matchMedia) return;
    var items = $$('.pp-announce__item', bar);
    if (items.length < 2) return;
    var phone = window.matchMedia('(max-width: 640px)');
    var still = window.matchMedia('(prefers-reduced-motion: reduce)');
    var index = 0;
    var paused = false;
    var timer = null;

    function show(i) {
      index = i % items.length;
      items.forEach(function (item, n) { item.classList.toggle('is-active', n === index); });
    }

    function start() {
      clearInterval(timer);
      bar.setAttribute('data-pp-rotating', '');
      show(index);
      if (still.matches) return;
      timer = setInterval(function () { if (!paused) show(index + 1); }, 4500);
    }

    function stop() {
      clearInterval(timer);
      bar.removeAttribute('data-pp-rotating');
    }

    function apply() { if (phone.matches) start(); else stop(); }

    ['mouseenter', 'focusin'].forEach(function (type) { bar.addEventListener(type, function () { paused = true; }); });
    ['mouseleave', 'focusout'].forEach(function (type) { bar.addEventListener(type, function () { paused = false; }); });
    if (phone.addEventListener) phone.addEventListener('change', apply);
    apply();
  }

  /* ------------------------------------------------ Rapid Search button */
  /* Rapid Search injects a floating search button. It stays where the app
     puts it (Tyler 2026-10-13: moving it into the mobile header made a
     squashed pill that read as a second search bar). The app's button is an
     icon with no text, so give it a name. */
  function initSearchButton() {
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      var button = document.getElementById('rps_sticky_widget');
      if (button && !button.getAttribute('aria-label')) button.setAttribute('aria-label', 'Search');
      if (button || tries > 40) clearInterval(timer);
    }, 500);
  }

  /* -------------------------------------------------------- fixed header */
  /* The header stays at the top on every screen size (pp-shopify.css makes
     its section sticky). Its height is shared as --pp-header-h so other
     sticky panels and in-page anchors sit below it. */
  function initHeaderHeight() {
    var header = $('.pp-header');
    if (!header) return;
    var root = document.documentElement;
    function set() {
      // Measure the closed header only: the open mobile menu is sized from
      // this value, so including it would make the two chase each other.
      if ($('.pp-nav.is-open', header)) return;
      root.style.setProperty('--pp-header-h', header.offsetHeight + 'px');
      bottom();
    }
    // Where the header ends on screen: below the announcement bar at the top
    // of the page, at its own height once that has scrolled away. The
    // floating search button sits just under it.
    var ticking = false;
    function bottom() {
      ticking = false;
      root.style.setProperty('--pp-header-bottom', Math.round(header.getBoundingClientRect().bottom) + 'px');
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(bottom); }
    }, { passive: true });
    set();
    window.addEventListener('resize', set);
    if (window.ResizeObserver) new ResizeObserver(set).observe(header);
  }

  /* ------------------------------------------------- SellUp cart upsells */
  /* SellUp's app embed drops its cart offers ("You may like this:") at the end
     of the cart form. Move them into the design's "A little something extra?"
     block, where the CSS restyles them as add-on rows. VIP Flower Rush is left
     out (the delivery-speed picker adds it). When SellUp has an offer, the
     theme's own add-on tiles are hidden; when it has none, they stay. */
  function initSellupCart() {
    var slot = $('[data-pp-sellup-cart]');
    if (!slot) return;
    var block = slot.closest('[data-pp-cart-addons]');
    var vipId = DELIVERY.vipHandle ? 'product-' + DELIVERY.vipHandle : null;

    function place() {
      var wrapper = document.getElementById('kartify_upsell_wrapper1');
      if (!wrapper) return false;
      if (wrapper.parentNode !== slot) slot.appendChild(wrapper);
      var offers = 0;
      $$('.productbox', wrapper).forEach(function (box) {
        var cell = box.closest('.owl-item') || box;
        var isVip = vipId && box.id === vipId;
        cell.classList.toggle('pp-sellup-vip', !!isVip);
        if (!isVip && !box.closest('.owl-item.cloned')) offers++;
      });
      slot.hidden = offers === 0;
      if (block) block.classList.toggle('has-sellup', offers > 0);
      return true;
    }

    // SellUp renders after the page loads and may redraw (its carousel), so
    // place it on arrival and again whenever the page changes for a while.
    var pending = null;
    var observer = new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(place, 50);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { observer.disconnect(); place(); }, 20000);
    place();
  }

  /* --------------------------------------------- collection filter + load */
  function initCollection() {
    var grid = $('[data-pp-grid]');
    if (!grid) return;

    var cards = $$('.pp-card', grid);
    var indicator = $('[data-pp-autoload]');
    var countEl = $('[data-pp-count]');
    var batch = (CONFIG.pagination && CONFIG.pagination.batchSize) || 8;
    var shown = batch;
    var activeChip = null;

    function matches(card) {
      if (!activeChip) return true;
      if (activeChip.kind === 'tag') {
        var tags = (card.getAttribute('data-pp-tags') || '').split(' ');
        return tags.indexOf(activeChip.value) !== -1;
      }
      if (activeChip.kind === 'max') {
        return parseFloat(card.getAttribute('data-pp-price')) < parseFloat(activeChip.value);
      }
      return true;
    }

    function paint() {
      var visible = cards.filter(matches);
      cards.forEach(function (card) { card.classList.add('is-hidden'); });
      visible.slice(0, shown).forEach(function (card) { card.classList.remove('is-hidden'); });
      if (countEl) countEl.textContent = visible.length;
      if (indicator) indicator.hidden = visible.length <= shown;
    }

    $$('[data-pp-chip]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        var kind = chip.getAttribute('data-pp-chip-kind');
        activeChip = (kind && kind !== 'all')
          ? { kind: kind, value: chip.getAttribute('data-pp-chip-value') }
          : null;
        shown = batch;
        $$('[data-pp-chip]').forEach(function (other) {
          other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
        });
        paint();
        fill();
      });
    });

    // Scroll-loaded reveal: no Load More button.
    // An IntersectionObserver only fires when the indicator crosses into view.
    // If a batch leaves it still in view (tall screens, fast scrolling), no new
    // event comes and loading stalls, so after each batch keep going while the
    // indicator is still near the viewport, then wait for the next scroll.
    function nearViewport() {
      if (!indicator || indicator.hidden) return false;
      return indicator.getBoundingClientRect().top < window.innerHeight + 240;
    }

    var filling = false;
    function fill() {
      if (filling) return;
      filling = true;
      (function step() {
        if (nearViewport() && cards.filter(matches).length > shown) {
          shown += batch;
          paint();
          window.requestAnimationFrame(step);
        } else {
          filling = false;
        }
      })();
    }

    if (indicator && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (entry) { return entry.isIntersecting; })) fill();
      }, { rootMargin: '240px' });
      observer.observe(indicator);
      window.addEventListener('scroll', fill, { passive: true });
      window.addEventListener('resize', fill);
    } else {
      shown = cards.length;
    }

    var sort = $('[data-pp-sort]');
    if (sort) {
      sort.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        window.location.href = url.toString();
      });
    }

    paint();
    fill();
  }

  /* -------------------------------------------------------- suburb check */
  /* "Do we deliver to your suburb?" (rebuilt 2026-11-02, client: not working).
     The answer comes from snippets/pp-delivery-areas.liquid: the postcodes in
     Shopify's local delivery zones (what checkout itself accepts), each with
     its suburbs. A postcode is checked directly; a suburb must match a name
     exactly; anything else offers "Did you mean" choices instead of guessing. */
  function initSuburbCheck() {
    var forms = $$('[data-pp-suburb-check]');
    if (!forms.length) return;

    var data = {};
    try { data = JSON.parse(($('#pp-delivery-areas') || {}).textContent || '{}').postcodes || {}; } catch (err) { data = {}; }
    var norm = function (text) { return String(text || '').toLowerCase().replace(/['’]/g, '').replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim(); };
    var suburbs = [];
    Object.keys(data).forEach(function (postcode) {
      data[postcode].forEach(function (name) { suburbs.push({ name: name, key: norm(name), postcode: postcode }); });
    });
    var phone = '<a class="pp-textlink pp-textlink--sand" href="tel:' + (CONFIG.phoneTel || '') + '">' + (CONFIG.phoneDisplay || '') + '</a>';
    var shop = '<a class="pp-textlink pp-textlink--sand" href="' + (CONFIG.shopUrl || '/collections/fresh-flowers') + '">Send flowers there</a>';
    var esc = function (text) { return String(text).replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; }); };

    forms.forEach(function (form) {
      var input = $('input', form);
      var result = form.parentNode.querySelector('[data-pp-suburb-result]') || form.nextElementSibling;
      if (!result) return;

      var yes = function (text) { result.innerHTML = 'Yes, we deliver to ' + esc(text) + '. ' + shop; result.classList.add('is-hit'); };
      var no = function (text) {
        result.innerHTML = (text ? esc(text) + ' is outside our delivery area, but call us on ' : 'We do not have that one listed. Call us on ')
          + phone + ' and we will see what we can do.';
        result.classList.remove('is-hit');
      };

      form.addEventListener('submit', function (event) {
        event.preventDefault();
        var raw = (input.value || '').trim();
        var query = norm(raw);
        result.classList.remove('is-hit');
        if (!query) { result.textContent = 'Type a suburb or postcode to check.'; return; }

        // A postcode anywhere in the entry ("4171", "Bulimba 4171") decides it.
        var code = (raw.match(/\b(\d{4})\b/) || [])[1];
        if (code) {
          if (!data[code]) return no(code);
          var names = data[code];
          return yes(names.slice(0, 3).join(', ') + (names.length > 3 ? ' and nearby' : '') + ' (' + code + ')');
        }

        var exact = suburbs.filter(function (s) { return s.key === query; })[0];
        if (exact) return yes(exact.name + ' (' + exact.postcode + ')');

        var close = query.length < 3 ? [] : suburbs.filter(function (s) {
          return s.key.indexOf(query) === 0 || s.key.indexOf(' ' + query) !== -1;
        }).slice(0, 5);
        if (!close.length) return no('');

        result.innerHTML = 'Did you mean: ' + close.map(function (s) {
          return '<button type="button" class="pp-check__pick" data-pp-pick="' + esc(s.name) + '">' + esc(s.name) + ' (' + s.postcode + ')</button>';
        }).join(' ');
      });

      result.addEventListener('click', function (event) {
        var pick = event.target.closest && event.target.closest('[data-pp-pick]');
        if (!pick) return;
        input.value = pick.getAttribute('data-pp-pick');
        form.dispatchEvent(new Event('submit', { cancelable: true }));
      });
    });
  }

  /* --------------------------------------------------------------- forms */
  /* Enquiry forms. Validation always runs. A form with an action (once it is
     connected to ActiveCampaign) then submits for real; a form without one
     shows the design's thank-you state and sends nothing. */
  function initForms() {
    $$('[data-pp-form]').forEach(function (form) {
      var error = $('[data-pp-form-error]', form);

      form.addEventListener('submit', function (event) {
        var missing = [];

        $$('[required]', form).forEach(function (field) {
          var empty = field.type === 'checkbox' ? !field.checked : !String(field.value || '').trim();
          field.classList.toggle('is-invalid', empty);
          if (empty) {
            var label = field.closest('.pp-field');
            var span = label ? $('span', label) : null;
            missing.push((span ? span.textContent : field.name).toLowerCase());
          }
        });

        // The forms are novalidate, so check an entered email looks like one
        // (same test as the pop-up) rather than sending "abc" to ActiveCampaign.
        var badEmail = $$('input[type="email"]', form).filter(function (field) {
          var value = String(field.value || '').trim();
          return value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        });
        badEmail.forEach(function (field) { field.classList.add('is-invalid'); });

        if (missing.length || badEmail.length) {
          event.preventDefault();
          if (error) {
            error.hidden = false;
            var parts = [];
            if (missing.length) parts.push('Please fill in: ' + missing.join(', ') + '.');
            if (badEmail.length) parts.push('Please enter a valid email address.');
            error.textContent = parts.join(' ');
          }
          var first = $('.is-invalid', form);
          if (first) first.focus();
          return;
        }

        if (error) error.hidden = true;
        event.preventDefault();

        if (form.getAttribute('action')) {
          if (form.hasAttribute('data-pp-busy')) return;
          if (form.hasAttribute('data-pp-fold')) foldExtras(form);
          form.setAttribute('data-pp-busy', '');
          var submit = $('[type="submit"]', form);
          if (submit) submit.disabled = true;
          acSubmit(form, function () {
            showDone();
          }, function (message) {
            form.removeAttribute('data-pp-busy');
            if (submit) submit.disabled = false;
            if (error) {
              error.hidden = false;
              error.textContent = message;
            }
          });
          return;
        }

        showDone();
      });

      function showDone() {
        var done = document.createElement('div');
        done.className = 'pp-form__done';
        done.setAttribute('role', 'status');
        done.setAttribute('tabindex', '-1');
        done.innerHTML = '<h2 class="pp-h3">Thank you, that is with us</h2>'
          + '<p>If it is urgent, call us on '
          + '<a href="tel:' + (CONFIG.phoneTel || '') + '">' + (CONFIG.phoneDisplay || '') + '</a>.</p>'
          + (form.hasAttribute('data-pp-unconnected')
            ? '<p><strong>Preview note:</strong> nothing was actually sent. This form '
              + 'needs connecting to ActiveCampaign before go-live.</p>'
            : '');
        form.parentNode.replaceChild(done, form);
        done.focus();
      }
    });
  }

  /* Send a connected form to ActiveCampaign the way its own embed does: a
     JSONP request to proc.php, so the visitor stays on the page and sees our
     thank-you. ActiveCampaign answers by calling window._show_thank_you or
     window._show_error with the form id; the reminder page's copy of its
     handler defines those too, so any id that is not ours is passed on. */
  var acPending = {};
  function acCallbacks() {
    if (window.__ppAcHooked) return;
    window.__ppAcHooked = true;
    var prevThanks = window._show_thank_you;
    var prevError = window._show_error;
    window._show_thank_you = function (id) {
      var job = acPending[id];
      if (job) { delete acPending[id]; job.ok(); return; }
      if (typeof prevThanks === 'function') prevThanks.apply(this, arguments);
    };
    window._show_error = function (id, message) {
      var job = acPending[id];
      if (job) { delete acPending[id]; job.fail(message); return; }
      if (typeof prevError === 'function') prevError.apply(this, arguments);
    };
  }

  function acSubmit(form, onOk, onFail) {
    acCallbacks();
    var idField = form.querySelector('input[name="u"]');
    var id = idField ? idField.value : '';
    var params = [];
    $$('input, select, textarea', form).forEach(function (field) {
      if (!field.name || field.disabled || field.name === 'pp_hp_4k2n' || field.name === 'tags') return;
      if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
      var value = field.value;
      if (field.name === 'phone') value = acPhone(value);
      params.push(encodeURIComponent(field.name) + '=' + encodeURIComponent(value));
    });
    params.push('jsonp=true');
    var failed = false;
    var timer = setTimeout(function () {
      failed = true;
      delete acPending[id];
      onFail('Sorry, that did not send. Please try again, or call us on ' + (CONFIG.phoneDisplay || '') + '.');
    }, 15000);
    acPending[id] = {
      ok: function () { if (failed) return; clearTimeout(timer); onOk(); },
      fail: function (message) {
        if (failed) return;
        clearTimeout(timer);
        var text = String(message || '').replace(/<[^>]*>/g, '').trim();
        onFail(text || 'Sorry, that did not send. Please check the form and try again.');
      }
    };
    var script = document.createElement('script');
    script.src = form.getAttribute('action') + '?' + params.join('&');
    script.async = true;
    script.onerror = function () { if (acPending[id]) acPending[id].fail(''); };
    document.body.appendChild(script);
  }

  /* ActiveCampaign rejects any phone number that is not in +country format.
     Australian numbers (0412 345 678, (07) 3399 9659, 61 412...) become
     +61412345678; anything already starting with + is only stripped of spaces. */
  function acPhone(value) {
    var raw = String(value || '').trim();
    if (!raw) return raw;
    if (raw.charAt(0) === '+') return '+' + raw.slice(1).replace(/\D/g, '');
    var digits = raw.replace(/\D/g, '');
    if (digits.indexOf('61') === 0) return '+' + digits;
    if (digits.charAt(0) === '0') return '+61' + digits.slice(1);
    return '+61' + digits;
  }

  /* Every answer now has its own ActiveCampaign field. As a safety net, any
     answer whose control does not post to a field[n] name (because the section's
     field map is missing a line) is written into the Enquiry Message before a
     connected form submits, so it still reaches the client. Runs once, so a
     double submit does not repeat it. */
  function foldExtras(form) {
    var message = $('[data-ac="Enquiry Message"]', form);
    if (!message || form.hasAttribute('data-pp-folded')) return;
    var standard = ['firstname', 'lastname', 'email', 'phone', 'first_name', 'last_name', 'tags', 'pp_hp_4k2n'];
    var lines = [];
    $$('[data-pp-label]', form).forEach(function (control) {
      if (control === message) return;
      var posted = control.tagName === 'FIELDSET'
        ? (($('input', control) || {}).name || '')
        : (control.name || '');
      if (/^field\[/.test(posted)) return;
      var value;
      if (control.tagName === 'FIELDSET') {
        value = $$('input:checked', control).map(function (i) { return i.value; }).join(', ');
      } else {
        if (standard.indexOf(control.name) !== -1) return;
        value = String(control.value || '').trim();
      }
      if (value) lines.push(control.getAttribute('data-pp-label') + ': ' + value);
    });
    if (!lines.length) return;
    var typed = String(message.value || '').trim();
    message.value = lines.join('\n') + (typed ? '\n\n' + typed : '');
    form.setAttribute('data-pp-folded', '');
  }

  /* ----------------------------------------------------------- carousels */
  /* "Our work" carousels. The track scrolls and snaps without JS; this adds
     the arrows and keeps them in step with the scroll position. */
  function initCarousels() {
    $$('.pp-carousel').forEach(function (carousel) {
      var track = carousel.querySelector('.pp-carousel__track');
      var prev = carousel.querySelector('.pp-carousel__btn--prev');
      var next = carousel.querySelector('.pp-carousel__btn--next');
      if (!track || !prev || !next) return;
      if (track.scrollWidth <= track.clientWidth + 4) return;
      carousel.classList.add('pp-carousel--ready');

      function step() {
        var slide = track.querySelector('.pp-carousel__slide');
        return slide ? slide.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
      }

      function sync() {
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      }

      prev.addEventListener('click', function () { track.scrollLeft -= step(); });
      next.addEventListener('click', function () { track.scrollLeft += step(); });
      track.addEventListener('scroll', sync);
      window.addEventListener('resize', sync);
      sync();
    });
  }

  /* ----------------------------------------------------------- read more */
  /* Collection intros: first paragraph shown, the rest behind a button. The
     full copy is always in the markup, so it is readable without JS and
     visible to crawlers. */
  function initReadMore() {
    $$('[data-pp-readmore]').forEach(function (block) {
      var body = block.querySelector('.pp-readmore__body');
      var btn = block.querySelector('[data-pp-readmore-btn]');
      if (!body || !btn) return;
      if (body.children.length < 2) return;

      var expanded = false;
      block.classList.add('pp-readmore--collapsed');
      btn.hidden = false;
      btn.setAttribute('aria-expanded', 'false');

      btn.addEventListener('click', function () {
        expanded = !expanded;
        block.classList.toggle('pp-readmore--collapsed', !expanded);
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded ? 'Read less' : 'Read more';
      });
    });
  }

  /* --------------------------------------------------------------- pop-up */
  /* Home page reminder pop-up, per the client's brief of 2026-09-17:
     exit intent OR a delay OR scroll depth, whichever is first, never on page
     load; once per visitor, remembered; a slide-in banner on mobile.
     Submission is held until an ActiveCampaign form exists for it: with no
     action the fields validate and the thank-you shows, and nothing is sent. */
  function initPopup() {
    var popup = $('[data-pp-popup]');
    if (!popup) return;
    if (window.Shopify && window.Shopify.designMode && !popup.hasAttribute('data-pp-popup-preview')) return;

    var key = 'pp-popup-seen';
    var days = parseInt(popup.getAttribute('data-pp-popup-days'), 10) || 90;
    var delay = parseInt(popup.getAttribute('data-pp-popup-delay'), 10) || 12000;
    var scrollPct = parseInt(popup.getAttribute('data-pp-popup-scroll'), 10) || 40;

    function seen() {
      try {
        var until = window.localStorage.getItem(key);
        return !!until && Number(until) > Date.now();
      } catch (err) { return false; }
    }

    function remember() {
      try {
        window.localStorage.setItem(key, String(Date.now() + days * 86400000));
      } catch (err) { /* private window: show it again next time */ }
    }

    if (seen() && !popup.hasAttribute('data-pp-popup-preview')) return;

    var shown = false;
    var timer = null;

    function close() {
      popup.classList.remove('is-open');
      popup.hidden = true;
      remember();
    }

    function reveal() { popup.classList.add('is-open'); }

    // Never cover the suburb checker (client 2026-11-02: on desktop the pop-up
    // sat over its Check button). While the checker is on screen or being
    // typed in, wait and try again.
    var waitTimer = null;
    function checkerBusy() {
      var checker = $('[data-pp-suburb-check]');
      if (!checker) return false;
      if (checker.contains(document.activeElement)) return true;
      var r = checker.getBoundingClientRect();
      return r.bottom > 0 && r.top < window.innerHeight;
    }

    function open() {
      if (shown) return;
      if (!popup.hasAttribute('data-pp-popup-preview') && checkerBusy()) {
        window.clearTimeout(waitTimer);
        waitTimer = window.setTimeout(open, 2000);
        return;
      }
      shown = true;
      popup.hidden = false;
      // Next frame so the transition runs; the timeout covers background tabs,
      // where requestAnimationFrame may never fire.
      window.requestAnimationFrame(reveal);
      window.setTimeout(reveal, 50);
      teardown();
    }

    function onExit(event) {
      if (event.clientY <= 0 && !event.relatedTarget) open();
    }

    function onScroll() {
      var height = document.documentElement.scrollHeight - window.innerHeight;
      if (height <= 0) return;
      if ((window.scrollY / height) * 100 >= scrollPct) open();
    }

    function teardown() {
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('mouseout', onExit);
      window.removeEventListener('scroll', onScroll);
    }

    if (popup.hasAttribute('data-pp-popup-preview')) {
      open();
    } else {
      timer = window.setTimeout(open, delay);
      document.addEventListener('mouseout', onExit);
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    $$('[data-pp-popup-close]', popup).forEach(function (button) {
      button.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && popup.classList.contains('is-open')) close();
    });

    var form = $('[data-pp-popup-form]', popup);
    if (!form) return;
    form.addEventListener('submit', function (event) {
      var error = $('[data-pp-popup-error]', popup);
      var missing = $$('input, select', form).filter(function (field) {
        return field.required && !String(field.value).trim();
      });
      var email = $('[name="email"]', form);
      if (email && email.value && !/.+@.+\..+/.test(email.value)) missing.push(email);
      $$('input, select', form).forEach(function (field) { field.classList.remove('is-invalid'); });
      if (missing.length) {
        event.preventDefault();
        missing.forEach(function (field) { field.classList.add('is-invalid'); });
        if (error) {
          error.textContent = 'Please fill in every field with a valid email.';
          error.hidden = false;
        }
        missing[0].focus();
        return;
      }
      if (error) error.hidden = true;
      event.preventDefault();
      function thank() {
        remember();
        form.hidden = true;
        var thanks = $('[data-pp-popup-thanks]', popup);
        if (thanks) thanks.hidden = false;
      }
      if (!form.getAttribute('action')) { thank(); return; }
      if (form.hasAttribute('data-pp-busy')) return;
      form.setAttribute('data-pp-busy', '');
      acSubmit(form, thank, function (message) {
        form.removeAttribute('data-pp-busy');
        if (error) {
          error.textContent = message;
          error.hidden = false;
        }
      });
    });
  }

  /* ------------------------------------------------------------- honeypot */
  /* Bots have been signing up through the reminder form (329 in July 2026).
     Any form whose hidden pp_hp_4k2n field has a value is a bot, so the submit
     is stopped in the capture phase, before ActiveCampaign's handler or ours
     sees it. Silently. Do not rename the field: autofill fills names like
     company, website, url and address, which would block real people. */
  function initHoneypot() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      var trap = form.querySelector('[name="pp_hp_4k2n"]');
      if (!trap || !String(trap.value || '').trim()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
  }

  /* ------------------------------------------------------ required fields */
  /* ActiveCampaign's script sets novalidate on the reminder form, so the
     browser never enforces "required", and a blank Title or Occasion reaches
     ActiveCampaign, which then stores its first option (Mr, Anniversary).
     Client 2026-10-14: both must be chosen. Forms with a
     [data-pp-required-error] slot are checked here in the capture phase,
     after the honeypot and before ActiveCampaign's handler. */
  function initRequiredFields() {
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      var error = form.querySelector('[data-pp-required-error]');
      if (!error) return;
      var missing = [];
      $$('[required]', form).forEach(function (field) {
        var empty;
        if (field.type === 'radio') {
          empty = !form.querySelector('[name="' + field.name + '"]:checked');
        } else if (field.type === 'email') {
          empty = !/.+@.+\..+/.test(String(field.value).trim());
        } else {
          empty = !String(field.value || '').trim();
        }
        var marker = field.closest('[data-pp-required-group]') || field;
        marker.classList.toggle('is-invalid', empty);
        if (empty) missing.push(marker);
      });
      if (!missing.length) {
        error.hidden = true;
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      var names = missing.map(function (marker) {
        var label = marker.querySelector('legend span, :scope > span') || marker.closest('label');
        var span = label && (label.tagName === 'LABEL' ? label.querySelector('span') : label);
        return span ? span.textContent.trim() : '';
      }).filter(Boolean);
      error.textContent = 'Please fill in: ' + names.join(', ') + '.';
      error.hidden = false;
      var focus = missing[0].matches('input, select') ? missing[0] : missing[0].querySelector('input, select');
      if (focus) focus.focus();
    }, true);

    // Clear a field's warning as soon as it is filled in.
    document.addEventListener('change', function (event) {
      var field = event.target;
      if (!field || !field.form || !field.form.querySelector('[data-pp-required-error]')) return;
      var marker = field.closest('[data-pp-required-group]') || field;
      marker.classList.remove('is-invalid');
    });
  }

  /* ------------------------------------------------------------ form tags */
  /* Conditional ActiveCampaign tags, e.g. Arbour Hire adds WEDDING INQUIRY
     when Event type is Wedding. The rule rides on the form as
     data-ac-tag-when; the resolved list is written to data-ac-tags-resolved
     and to any hidden input named "tags", for the integration to read. */
  function initFormTags() {
    $$('[data-ac-tag-when]').forEach(function (form) {
      var rules;
      try { rules = JSON.parse(form.getAttribute('data-ac-tag-when') || '{}'); } catch (err) { return; }
      // Rules name a control by its ActiveCampaign field (data-ac), which
      // survives connection, or by its input name.
      function findControl(key) {
        return form.querySelector('[data-ac="' + key + '"]') || form.querySelector('[name="' + key + '"]');
      }
      var base = (form.getAttribute('data-ac-tags') || '')
        .split(',').map(function (t) { return t.trim(); }).filter(Boolean);

      function resolve() {
        var tags = base.slice();
        Object.keys(rules).forEach(function (name) {
          var control = findControl(name);
          var extra = control && rules[name][control.value];
          if (extra && tags.indexOf(extra) === -1) tags.push(extra);
        });
        form.setAttribute('data-ac-tags-resolved', tags.join(', '));
        var hidden = form.querySelector('input[type="hidden"][name="tags"]');
        if (hidden) hidden.value = tags.join(', ');
      }

      Object.keys(rules).forEach(function (name) {
        var control = findControl(name);
        if (control) control.addEventListener('change', resolve);
      });
      resolve();
    });
  }

  /* ---------------------------------------------------------------- boot */
  function init() {
    initHoneypot();
    initRequiredFields();
    initNav();
    initDeliveryRows();
    initProduct();
    initSellupVip();
    initAddButtons();
    initCartPage();
    initSellupCart();
    initSearchButton();
    initHeaderHeight();
    initAnnouncement();
    initCollection();
    initSuburbCheck();
    initForms();
    initFormTags();
    initCarousels();
    initReadMore();
    initPopup();

    // Re-check the VIP window every minute so a page left open does not keep
    // offering a service that has closed.
    setInterval(paintExpressWindow, 60000);
  }

  // Re-run section behaviour when a section is edited in the theme editor.
  document.addEventListener('shopify:section:load', function () {
    initCarousels();
    initReadMore();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
